import {
  Box,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { friendlyDateTime } from "Helpers/dates";
import { useCallback, useEffect, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { Button } from "@mui/material";
import {
  ApiComponentType,
  ApiMediaType,
  ApiSimpleComponent,
} from "@incendium/api";
import Loading from "Components/Loading/Loading";
import { useSelectedProject } from "Hooks";
import { generateComponent } from "features/campaigns/services/componentService";
import UploaderButton from "features/media/components/UploaderButton";

interface NewBlockDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (name: string, component?: ApiSimpleComponent) => void;
  /** When true, only show the AI generation option (no "Create blank" switch). Used when creating from the page editor sidebar. */
  aiOnly?: boolean;
}

function NewBlockDialog({ open, onClose, onConfirm, aiOnly = false }: NewBlockDialogProps) {
  const defaultName = `Block (${friendlyDateTime(new Date())})`;
  const [name, setName] = useState(defaultName);
  const [generateWithAI, setGenerateWithAI] = useState(aiOnly);
  const [prompt, setPrompt] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFileName, setImageFileName] = useState<string | null>(null);
  const [isAdvanced, setIsAdvanced] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { selectedProject } = useSelectedProject();

  useEffect(() => {
    if (!open) {
      setName(defaultName);
      setGenerateWithAI(aiOnly);
      setPrompt("");
      setImageUrl("");
      setImageFileName(null);
      setError(null);
      setIsAdvanced(false);
    }
  }, [open, aiOnly]);

  useEffect(() => {
    if (aiOnly) setGenerateWithAI(true);
  }, [aiOnly]);

  const handleSubmit = useCallback(async () => {
    if (isGenerating || !selectedProject?.id) return;

    if (generateWithAI || aiOnly) {
      if (!prompt || prompt.trim().length < 10) {
        setError("Prompt is required and must be at least 10 characters");
        return;
      }
    }

    setIsGenerating(true);
    setError(null);

    try {
      if (generateWithAI || aiOnly) {
        const res = await generateComponent(
          selectedProject.id,
          name,
          prompt.trim(),
          ApiComponentType.LANDING_BLOCK,
          imageUrl || undefined,
          undefined,
          undefined,
          isAdvanced,
        );
        if (res.component) {
          onConfirm(name, res.component);
          onClose();
        } else {
          setError("No block returned from AI");
        }
      } else {
        onConfirm(name);
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create block");
    } finally {
      setIsGenerating(false);
    }
  }, [
    name,
    prompt,
    imageUrl,
    generateWithAI,
    isAdvanced,
    selectedProject?.id,
    onConfirm,
    onClose,
    isGenerating,
    aiOnly,
  ]);

  const isFormValid =
    name &&
    name.trim().length >= 3 &&
    name.length <= 200 &&
    (!(generateWithAI || aiOnly) || (prompt && prompt.trim().length >= 10));

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ValidatorForm onSubmit={handleSubmit}>
        <DialogTitle>Create New Lightning Block</DialogTitle>
        {isGenerating && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              zIndex: 1,
            }}
          >
            <Loading />
          </Box>
        )}
        <DialogContent>
          <TextValidator
            label="Block name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            name="name"
            fullWidth
            autoFocus
            validators={[
              "required",
              "minStringLength:3",
              "maxStringLength:200",
            ]}
            errorMessages={[
              "Name is required",
              "Name must be 3 or more characters",
              "Name must be less than 200 characters",
            ]}
            disabled={isGenerating}
            sx={{ mb: 2 }}
          />

          {!aiOnly && (
            <FormControlLabel
              control={
                <Switch
                  checked={generateWithAI}
                  onChange={(e) => setGenerateWithAI(e.target.checked)}
                  disabled={isGenerating}
                />
              }
              label="Generate with AI"
              sx={{ mb: 2, display: "block" }}
            />
          )}

          {(generateWithAI || aiOnly) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextValidator
                label="Describe the block"
                value={prompt}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPrompt(e.target.value)
                }
                name="prompt"
                fullWidth
                multiline
                minRows={3}
                validators={["required", "minStringLength:10"]}
                errorMessages={[
                  "Prompt is required",
                  "Prompt must be at least 10 characters (e.g. “A hero section with headline, subtext and CTA button”)",
                ]}
                disabled={isGenerating}
                placeholder="e.g. A hero section with headline, subtext and CTA button"
              />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Reference image (optional)
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Upload an image for design inspiration. The AI will use it as
                  a visual reference.
                </Typography>
                <UploaderButton
                  tmp
                  useDropZone
                  dropZoneText="Drag and drop an image here or click to browse"
                  accept="image/*"
                  project={selectedProject!}
                  path=""
                  type={ApiMediaType.IMAGE_MEDIA}
                  hidden
                  uploading={imageUploading}
                  text="Uploading..."
                  previewUrl={imageUrl || undefined}
                  previewFileName={imageFileName || undefined}
                  onProgress={(percent, status) => {
                    setImageUploading(percent < 100 && status !== "finished");
                  }}
                  onFinish={(result, file) => {
                    setImageUrl(result.readUrl ?? result.publicUrl ?? "");
                    setImageFileName(file.name);
                    setImageUploading(false);
                  }}
                  onError={() => setImageUploading(false)}
                />
              </Box>
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isAdvanced}
                      onChange={() => setIsAdvanced(!isAdvanced)}
                    />
                  }
                  label="Enable premium build mode"
                  value={isAdvanced}
                />
                <Typography variant="body2">
                  Best for complex, or more creative blocks. Uses a more
                  powerful model and in-depth coding approach, so requires more
                  credits.
                </Typography>
              </Stack>
            </Box>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isGenerating}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isGenerating || !isFormValid}
          >
            {(generateWithAI || aiOnly) && isGenerating
              ? "Generating…"
              : generateWithAI || aiOnly
                ? "Generate block"
                : "Create"}
          </Button>
        </DialogActions>
      </ValidatorForm>
    </CustomDialog>
  );
}

export default NewBlockDialog;
